/**
 * PAGES REFRESH WHEN YOU COME BACK TO THE TAB (Jeremy, 1 Nov 2026, punch list
 * F172: "the young ones are not used to hitting refresh").
 *
 * Every page loads its data once, when it opens. Somebody who leaves Canopy
 * open in a tab, works in Outlook for an hour and comes back was looking at
 * the hour-old version, with nothing on screen saying so. Now, when the tab
 * becomes visible again (or the window takes focus), the page on screen reads
 * its data again -- QUIETLY: the old answer stays on screen until the new one
 * replaces it, so there is no "Loading…" flash and no jump in the scroll.
 *
 * ## Never while somebody is editing
 *
 * A re-read hands a page fresh objects, and some editors re-sync their boxes
 * from what they were given (the deliverable page does). So a return does
 * nothing while the page shows somebody mid-edit, by three signals read from
 * the document rather than from a list every editor would have to join:
 *
 * - **a pop-up with a field in it** -- the block reason, Plan all stages, Edit
 *   project. A pop-up with nothing to type into (Read all instructions, My
 *   calendar) is reading, and a refresh behind it is harmless;
 * - **the cursor in a field** -- where it still is after switching tabs mid-word;
 * - **typed text not yet saved** -- a field somebody has typed into that is
 *   still on the page and not empty. A saved inline editor closes, taking its
 *   field with it; a posted note empties its box.
 *
 * Search boxes (`type="search"`), checkboxes and drop-downs used as filters
 * are not editing: a filter left with the cursor in it should not stop the
 * list behind it from refreshing.
 *
 * ## How often
 *
 * At most once every 15 seconds per page, and never in the first 15 seconds
 * after it opened -- it has just read its data. Returning fires BOTH
 * `visibilitychange` and `focus`; they are collapsed into one check a moment
 * later.
 */
import { useEffect, useState } from "react";

/** The shortest gap between two refreshes of one page. */
export const MIN_GAP_MS = 15_000;

/** How long after the first return signal the check runs, so `focus` and `visibilitychange` make one. */
export const SETTLE_MS = 250;

/** Whether enough time has passed since the last read for another. */
export function dueForRefresh(now: number, last: number, gap: number = MIN_GAP_MS): boolean {
  return now - last >= gap;
}

/** The parts of an element the editing check reads, so it can be tested without a browser. */
export interface FieldLike {
  readonly tagName: string;
  readonly type?: string;
  readonly isContentEditable?: boolean;
  readonly value?: string;
  readonly isConnected?: boolean;
}

/** Input types that are not typed into: clicked, or a filter. */
const NOT_TYPED = new Set([
  "search",
  "checkbox",
  "radio",
  "button",
  "submit",
  "reset",
  "range",
  "color",
  "file",
  "image",
  "hidden",
]);

/** A control somebody types into: a text-like input, a textarea, or editable text. */
export function isTypedField(el: FieldLike | null | undefined): boolean {
  if (!el) return false;
  if (el.isContentEditable) return true;
  const tag = el.tagName.toUpperCase();
  if (tag === "TEXTAREA") return true;
  if (tag !== "INPUT") return false;
  return !NOT_TYPED.has((el.type || "text").toLowerCase());
}

/** A field that makes a pop-up a FORM: anything typed into, or a drop-down. */
export function isFormField(el: FieldLike | null | undefined): boolean {
  if (!el) return false;
  return isTypedField(el) || el.tagName.toUpperCase() === "SELECT";
}

/** What the document shows, reduced to what the editing check needs. */
export interface EditingSignals {
  /** Every form control inside an open pop-up. */
  readonly dialogFields: readonly FieldLike[];
  /** Where the cursor is. */
  readonly active: FieldLike | null;
  /** Fields somebody has typed into since the page loaded. */
  readonly touched: readonly FieldLike[];
}

/** Whether somebody is in the middle of editing. */
export function isEditing(s: EditingSignals): boolean {
  if (s.dialogFields.some(isFormField)) return true;
  if (isTypedField(s.active)) return true;
  return s.touched.some((t) => t.isConnected !== false && isTypedField(t) && (t.value ?? "") !== "");
}

/* ── The browser side ──────────────────────────────────────────────────── */

/**
 * Typed-into fields, for the "typed text not yet saved" signal. A field that
 * has left the page is dropped at the next check.
 */
let touched: Set<Element> | null = null;

function trackTyping(): void {
  if (touched !== null || typeof document === "undefined") return;
  const set = new Set<Element>();
  touched = set;
  document.addEventListener(
    "input",
    (e) => {
      const el = e.target;
      if (el instanceof Element && isTypedField(el as unknown as FieldLike)) set.add(el);
    },
    true,
  );
  // A submitted form has saved what was typed in it.
  document.addEventListener(
    "submit",
    (e) => {
      const form = e.target;
      if (!(form instanceof Element)) return;
      for (const el of set) if (form.contains(el)) set.delete(el);
    },
    true,
  );
}

/** The editing check, read from the live document. */
export function editingInDocument(): boolean {
  if (typeof document === "undefined") return false;
  const dialogFields = Array.from(
    document.querySelectorAll(
      '[role="dialog"] input, [role="dialog"] textarea, [role="dialog"] select, [role="dialog"] [contenteditable="true"], dialog[open] input, dialog[open] textarea, dialog[open] select',
    ),
  ) as unknown as FieldLike[];
  const live: FieldLike[] = [];
  for (const el of touched ?? []) {
    if (el.isConnected) live.push(el as unknown as FieldLike);
    else touched?.delete(el);
  }
  return isEditing({
    dialogFields,
    active: document.activeElement as unknown as FieldLike | null,
    touched: live,
  });
}

/**
 * A number that goes up each time the page should quietly read its data
 * again. Put it in the dependencies of the effect that loads the page:
 *
 *     const back = useRefreshOnReturn();
 *     useEffect(() => { ...load... }, [fetcher, back]);
 *
 * `whileEditing` is for counts in the header -- the Inbox badge, the Needs
 * attention flag -- which change nothing anybody is typing into.
 */
export function useRefreshOnReturn(options?: { whileEditing?: boolean }): number {
  const whileEditing = options?.whileEditing === true;
  const [tick, setTick] = useState(0);

  useEffect(() => {
    trackTyping();
    let last = Date.now();
    let timer: number | undefined;

    const check = (): void => {
      timer = undefined;
      if (document.visibilityState !== "visible") return;
      const now = Date.now();
      if (!dueForRefresh(now, last)) return;
      if (!whileEditing && editingInDocument()) return;
      last = now;
      setTick((n) => n + 1);
    };
    const onReturn = (): void => {
      if (timer === undefined) timer = window.setTimeout(check, SETTLE_MS);
    };

    document.addEventListener("visibilitychange", onReturn);
    window.addEventListener("focus", onReturn);
    return () => {
      document.removeEventListener("visibilitychange", onReturn);
      window.removeEventListener("focus", onReturn);
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [whileEditing]);

  return tick;
}
